import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Field, H4, Input, Select, Stack, Title } from "@introist/react-foundation/v2";
import { TopBarRoute } from "components/templates";
import { ListRow } from "components/organisms";
import { api } from "services/rpc/RpcProvider";
import styled from "styled-components";

const SectionTitle = styled(H4)`
  margin-top: 24px;
  margin-bottom: 8px;
`;

const MatchedValues = styled(Title)`
  color: var(--color-foreground-dimmed);
  font-size: 12px;
`;

export const SearchRoute = () => {
  const navigate = useNavigate();

  const [selectedFieldId, setSelectedFieldId] = useState<string | undefined>();
  const [valueFilter, setValueFilter] = useState("");

  const { data: fields } = api.employees.fields.list.useQuery({});

  const { data: results, isFetching } = api.search.attributeUsages.useQuery(
    { fieldId: selectedFieldId!, value: valueFilter || undefined },
    { enabled: !!selectedFieldId }
  );

  const fieldOptions = (fields ?? []).map(f => ({ key: f.id, title: f.name }));

  const hasResults =
    results && (results.workflowSteps.length > 0 || results.triggers.length > 0);

  return (
    <TopBarRoute paths={[{ key: "search", title: "Search" }]} loading={isFetching}>
      <Stack vertical style={{ maxWidth: 720, padding: "8px 0" }}>
        <Stack gap="small">
          <div style={{ width: 260 }}>
            <Field title="Attribute">
              <Select
                size="small"
                style={{ width: "100%" }}
                searchable
                placeholder="Select attribute..."
                options={fieldOptions}
                value={selectedFieldId}
                onSelect={opt => {
                  setSelectedFieldId(opt.key);
                  setValueFilter("");
                }}
              />
            </Field>
          </div>
          <div style={{ width: 220 }}>
            <Field title="Value filter">
              <Input
                size="small"
                placeholder="Filter by value..."
                value={valueFilter}
                onChange={setValueFilter}
                disabled={!selectedFieldId}
              />
            </Field>
          </div>
        </Stack>

        {selectedFieldId && results && !hasResults && (
          <Title style={{ marginTop: 24, color: "var(--color-foreground-dimmed)" }}>
            No usages found
          </Title>
        )}

        {results && results.workflowSteps.length > 0 && (
          <Stack vertical gap="xSmall">
            <SectionTitle>Workflow steps</SectionTitle>
            {results.workflowSteps.map(step => (
              <ListRow
                key={step.stepId}
                gridTemplateColumns="1fr auto"
                clickable
                onClick={() => navigate(`/flows/edit/${step.workflowId}/${step.stepId}`)}
              >
                <Stack vertical gap="xSmall">
                  <Title variant="bold">{step.stepTitle || "Unnamed step"}</Title>
                  <Title style={{ fontSize: 12, color: "var(--color-foreground-dimmed)" }}>
                    {step.workflowName}
                  </Title>
                </Stack>
                {step.matchedValues.length > 0 && (
                  <MatchedValues>{step.matchedValues.join(", ")}</MatchedValues>
                )}
              </ListRow>
            ))}
          </Stack>
        )}

        {results && results.triggers.length > 0 && (
          <Stack vertical gap="xSmall">
            <SectionTitle>Triggers</SectionTitle>
            {results.triggers.map(trigger => (
              <ListRow
                key={trigger.triggerId}
                gridTemplateColumns="1fr auto"
                clickable
                onClick={() => navigate(`/triggers/${trigger.triggerId}`)}
              >
                <Stack vertical gap="xSmall">
                  <Title variant="bold">{trigger.triggerName}</Title>
                  {trigger.workflowName && (
                    <Title style={{ fontSize: 12, color: "var(--color-foreground-dimmed)" }}>
                      {trigger.workflowName}
                    </Title>
                  )}
                </Stack>
                {trigger.matchedValues.length > 0 && (
                  <MatchedValues>{trigger.matchedValues.join(", ")}</MatchedValues>
                )}
              </ListRow>
            ))}
          </Stack>
        )}
      </Stack>
    </TopBarRoute>
  );
};
